/**
 * SY-02.2 consent: stored with a version number, changeable from settings.
 * Bumping CONSENT_VERSION (new purposes or vendors) invalidates older records so
 * the tool asks again. The API keeps the history (POST ConsentRequest).
 */
import type { ConsentRequest } from '../schemas.js';
import { readJson, writeJson, type KV } from '../storage.js';
import { consentModel, type Region } from './region.js';

export const CONSENT_VERSION = 1;

export interface ConsentChoice {
  analytics: boolean;
  ads: boolean;
  personalizedAds: boolean;
}

export interface ConsentRecord extends ConsentChoice {
  version: number;
  region: Region;
  updatedAt: number;
}

export class ConsentStore {
  private readonly listeners = new Set<(r: ConsentRecord) => void>();

  constructor(
    private readonly kv: KV,
    readonly version: number = CONSENT_VERSION,
    private readonly key = 'consent',
  ) {}

  /** The stored record, or null when absent or from an older version. */
  current(): ConsentRecord | null {
    const r = readJson<ConsentRecord>(this.kv, this.key);
    return r && r.version >= this.version ? r : null;
  }

  needsPrompt(region: Region): boolean {
    return consentModel(region) !== 'none' && this.current() === null;
  }

  set(choice: ConsentChoice, region: Region, now: number): ConsentRecord {
    // personalised ads need ads consent
    const rec: ConsentRecord = { ...choice, personalizedAds: choice.ads && choice.personalizedAds, version: this.version, region, updatedAt: now };
    writeJson(this.kv, this.key, rec);
    for (const fn of this.listeners) fn(rec);
    return rec;
  }

  /** What may run now: opt-in regions track nothing until a choice exists; elsewhere the default is on. */
  effective(region: Region): ConsentChoice {
    const r = this.current();
    if (r) return { analytics: r.analytics, ads: r.ads, personalizedAds: r.personalizedAds };
    const on = consentModel(region) !== 'opt-in';
    return { analytics: on, ads: on, personalizedAds: on };
  }

  onChange(fn: (r: ConsentRecord) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}

/** Body for the API's consent endpoint. */
export function toConsentRequest(r: ConsentRecord): ConsentRequest {
  return { version: r.version, analytics: r.analytics, ads: r.ads, personalizedAds: r.personalizedAds, region: r.region };
}
